import React, { useState, useMemo, useCallback } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Checkbox } from '@heartinternet/hi-ui';
import { Stepper } from '@heartinternet/hi-ui';
import { Icon } from '@heartinternet/hi-ui';
import { Step1Fields } from '../HostingPackageTypeForm/Step1Fields';
import { Step2WebTools } from '../HostingPackageTypeForm/Step2WebTools';
import { Step3WebApps } from '../HostingPackageTypeForm/Step3WebApps';
import type { HostingPackageTypeEditPageProps, ResoldType, WebServerType } from './types';
import './HostingPackageTypeEditPage.css';

export type { HostingPackageTypeEditPageProps };

const CGI_URL = '/manage/manage-package-types.cgi';

const STEPS = [
  { id: 'features', label: 'Package Features' },
  { id: 'web-tools', label: 'Web Tools' },
  { id: 'web-apps', label: 'Web Apps' },
] as const;

type StepId = (typeof STEPS)[number]['id'];

interface FormState {
  name: string;
  webspace: number | null;
  bandwidth: number | null;
  databases: number | null;
  subdomains: number | null;
  forwarders: number | null;
  responders: number | null;
  lists: number | null;
  mailboxes: number | null;
  ftpUsers: number | null;
  features: Set<string>;
  installables: Set<string>;
  installAutomatically: string;
  welcomeEmail: number | '';
  extendPasswordResetEmail: number | '';
  extendDomaincpSwitcher: boolean;
  sync: boolean;
  delete: boolean;
}

function hashKeys(h: Record<string, 1> | null | undefined): Set<string> {
  return new Set(Object.keys(h ?? {}));
}

function findAutoInstall(installables: Set<string>): string {
  for (const code of installables) {
    if (code.startsWith('-autoinstall=')) return code;
  }
  return '';
}

function initialFormState(rt: ResoldType): FormState {
  const installablesAll = hashKeys(rt.installables_h);
  const autoInstall = findAutoInstall(installablesAll);
  const installablesWithoutAuto = new Set(installablesAll);
  if (autoInstall) installablesWithoutAuto.delete(autoInstall);

  return {
    name: rt.name,
    webspace: rt.disc_space,
    bandwidth: rt.bandwidth,
    databases: rt.databasen,
    subdomains: rt.subdomains,
    forwarders: rt.forwarders,
    responders: rt.responders,
    lists: rt.lists,
    mailboxes: rt.mailboxes,
    ftpUsers: rt.ftp_users,
    features: hashKeys(rt.features_h),
    installables: installablesWithoutAuto,
    installAutomatically: autoInstall,
    welcomeEmail: rt.welcome_email_id ?? '',
    extendPasswordResetEmail: rt.extend_password_reset_email_id ?? '',
    extendDomaincpSwitcher: Boolean(rt.enable_extend_domaincp_switcher),
    sync: false,
    delete: false,
  };
}

function bannerFromDoneValue(
  done: string | null | undefined
): 'add' | 'edit' | 'delete' | undefined {
  if (done === 'add' || done === 'edit' || done === 'delete') return done;
  return undefined;
}

export const HostingPackageTypeEditPage: React.FC<HostingPackageTypeEditPageProps> = (props) => {
  const {
    editResoldType,
    resoldTypeProto,
    emails,
    eprEmails,
    usedPackageNames,
    inUse,
    hasSnapshots,
    snapshotsEnabled,
    featureCategoriesByType,
    featureToName,
    cgiOrderByType,
    cgiToName,
    done,
  } = props;

  const serverType: WebServerType = editResoldType.web_server_type;
  const featuresFieldName = `features${editResoldType.id}`;
  const installablesFieldName = `installables${editResoldType.id}`;

  const [activeStep, setActiveStep] = useState<StepId>('features');
  const [form, setForm] = useState<FormState>(() => initialFormState(editResoldType));
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const banner = useMemo<'add' | 'edit' | 'delete' | undefined>(() => {
    if (done) return done;
    const params = new URLSearchParams(window.location.search);
    return bannerFromDoneValue(params.get('done'));
  }, [done]);

  const usedNamesLower = useMemo(
    () => new Set(usedPackageNames.map((n) => n.trim().toLowerCase())),
    [usedPackageNames]
  );

  const validateName = useCallback(
    (name: string): string | null => {
      const trimmed = name.trim();
      if (!trimmed) return 'Please enter a package name.';
      if (usedNamesLower.has(trimmed.toLowerCase())) {
        return 'A package with that name already exists.';
      }
      return null;
    },
    [usedNamesLower]
  );

  const validateStepOne = useCallback((): boolean => {
    const err = validateName(form.name);
    setNameError(err);
    return err === null;
  }, [form.name, validateName]);

  const goToStep = useCallback(
    (target: StepId) => {
      const targetIndex = STEPS.findIndex((s) => s.id === target);
      const currentIndex = STEPS.findIndex((s) => s.id === activeStep);

      // Backward navigation (or staying put): always allowed.
      if (targetIndex <= currentIndex) {
        setActiveStep(target);
        return;
      }
      // Forward: validate the current step.
      if (activeStep === 'features' && !validateStepOne()) return;
      // Steps 2/3 currently have no required-field validation.
      setActiveStep(target);
    },
    [activeStep, validateStepOne]
  );

  const handleNext = () => {
    const currentIndex = STEPS.findIndex((s) => s.id === activeStep);
    const next = STEPS[currentIndex + 1];
    /* v8 ignore start -- Next button hidden on the final step, so `next` is always defined */
    if (next) goToStep(next.id);
    /* v8 ignore stop */
  };

  const handleBack = () => {
    const currentIndex = STEPS.findIndex((s) => s.id === activeStep);
    const prev = STEPS[currentIndex - 1];
    /* v8 ignore start -- Back button hidden on the first step, so `prev` is always defined */
    if (prev) goToStep(prev.id);
    /* v8 ignore stop */
  };

  const toggleFeature = (code: string, on: boolean) => {
    setForm((s) => {
      const next = new Set(s.features);
      if (on) next.add(code);
      else next.delete(code);
      return { ...s, features: next };
    });
  };

  const toggleInstallable = (code: string, on: boolean) => {
    setForm((s) => {
      const next = new Set(s.installables);
      if (on) next.add(code);
      else next.delete(code);
      return { ...s, installables: next };
    });
  };

  const buildSubmitBody = useCallback((): URLSearchParams => {
    const body = new URLSearchParams();
    body.set('id', String(editResoldType.id));
    body.set('name', form.name.trim());
    body.set('web_server_type', serverType);
    body.set('webspace', form.webspace === null ? '' : String(form.webspace));
    body.set('bandwidth', form.bandwidth === null ? '' : String(form.bandwidth));
    body.set('databases', form.databases === null ? '' : String(form.databases));
    body.set('subdomains', form.subdomains === null ? '' : String(form.subdomains));
    body.set('forwarders', form.forwarders === null ? '' : String(form.forwarders));
    body.set('responders', form.responders === null ? '' : String(form.responders));
    body.set('lists', form.lists === null ? '' : String(form.lists));
    body.set('mailboxes', form.mailboxes === null ? '' : String(form.mailboxes));
    body.set('ftp_users', form.ftpUsers === null ? '' : String(form.ftpUsers));
    body.set('welcome_email', String(form.welcomeEmail));
    body.set('extend_password_reset_email', String(form.extendPasswordResetEmail));
    if (form.extendDomaincpSwitcher) body.set('extend_domaincp_switcher', 'enabled');
    for (const code of form.features) body.append(featuresFieldName, code);
    for (const code of form.installables) body.append(installablesFieldName, code);
    if (form.installAutomatically) body.append(installablesFieldName, form.installAutomatically);
    if (form.sync) body.set('synch', String(editResoldType.id));
    if (form.delete) body.set('delete', String(editResoldType.id));
    return body;
  }, [editResoldType.id, form, serverType, featuresFieldName, installablesFieldName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStepOne()) {
      setActiveStep('features');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const body = buildSubmitBody();
      const isDelete = form.delete;
      const targetUrl = `${CGI_URL}?edit_id=${editResoldType.id}`;
      const res = await fetch(targetUrl, {
        method: 'POST',
        body,
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      });
      if (!res.ok && res.status !== 302) {
        throw new Error(`Save failed (HTTP ${res.status}).`);
      }
      if (isDelete) {
        window.location.assign(`${CGI_URL}?done=delete`);
      } else {
        window.location.assign(`${CGI_URL}?edit_id=${editResoldType.id}&done=edit`);
      }
    } catch (err) {
      setSubmitting(false);
      setSubmitError(err instanceof Error ? err.message : 'Unexpected error while saving.');
    }
  };

  const stepperSteps = STEPS.map((s) => ({
    id: s.id,
    label: s.label,
  }));

  return (
    <div className="hi-ui-hosting-package-type-edit-page">
      {banner === 'edit' && (
        <Notice variant="success" className="hi-ui-hosting-package-type-edit-page__success">
          You&apos;ve just updated a web hosting package type! Click{' '}
          <Link href="/manage/manage-reseller-add.cgi">here</Link> to create a shared hosting
          account based on this package.
        </Notice>
      )}
      {banner === 'add' && (
        <Notice variant="success" className="hi-ui-hosting-package-type-edit-page__success">
          Package completion successful. Click{' '}
          <Link href="/manage/manage-reseller-add.cgi">here</Link> to create a shared hosting
          account based on this package.
        </Notice>
      )}

      <Text variant="heading-1">Edit Hosting Package</Text>
      <Text variant="body">
        Update the details of this web hosting package. Changes apply immediately; existing accounts
        using this package can be re-synchronised below.
      </Text>

      <form onSubmit={handleSubmit} className="hi-ui-hosting-package-type-edit-page__form">
        <input type="hidden" name="id" value={editResoldType.id} />
        <input type="hidden" name="web_server_type" value={serverType} />

        <div className="hi-ui-hosting-package-type-edit-page__header-strip">
          <Text variant="body" inline>
            <strong>Id:</strong> {editResoldType.id}
          </Text>
          <span className="hi-ui-hosting-package-type-edit-page__os-indicator">
            <Icon name={serverType === 'windows' ? 'windows' : 'linux'} size={20} />
            <Text variant="body" inline>
              {serverType === 'windows' ? 'Windows' : 'Linux'}
            </Text>
          </span>
          {inUse ? (
            <Checkbox
              name="synch-toggle"
              checked={form.sync}
              onChange={(e) => setForm((s) => ({ ...s, sync: e.target.checked }))}
              label="Synchronise accounts using this package"
            />
          ) : (
            <Checkbox
              name="delete-toggle"
              checked={form.delete}
              onChange={(e) => setForm((s) => ({ ...s, delete: e.target.checked }))}
              label="Delete this package"
            />
          )}
        </div>

        <div className="hi-ui-hosting-package-type-form__stepper">
          <Stepper
            steps={stepperSteps}
            currentStep={activeStep}
            onStepChange={(id) => goToStep(id as StepId)}
            completedClickable
          />
        </div>

        {activeStep === 'features' && (
          <div className="hi-ui-hosting-package-type-edit-page__step">
            <Step1Fields
              serverType={serverType}
              resoldTypeProto={resoldTypeProto}
              emails={emails}
              eprEmails={eprEmails}
              hasSnapshots={hasSnapshots}
              snapshotsEnabled={snapshotsEnabled}
              featuresFieldName={featuresFieldName}
              values={form}
              nameError={nameError}
              onChange={(patch) => setForm((s) => ({ ...s, ...patch }))}
              onToggleFeature={toggleFeature}
              onClearNameError={() => setNameError(null)}
            />
          </div>
        )}

        {activeStep === 'web-tools' && (
          <div className="hi-ui-hosting-package-type-edit-page__step">
            <Step2WebTools
              serverType={serverType}
              featureCategoriesByType={featureCategoriesByType}
              featureToName={featureToName}
              featuresFieldName={featuresFieldName}
              features={form.features}
              onToggleFeature={toggleFeature}
            />
          </div>
        )}

        {activeStep === 'web-apps' && (
          <div className="hi-ui-hosting-package-type-edit-page__step">
            <Step3WebApps
              serverType={serverType}
              cgiOrderByType={cgiOrderByType}
              cgiToName={cgiToName}
              installablesFieldName={installablesFieldName}
              installables={form.installables}
              installAutomatically={form.installAutomatically}
              onToggleInstallable={toggleInstallable}
              onInstallAutomaticallyChange={(value) =>
                setForm((s) => ({ ...s, installAutomatically: value }))
              }
            />
          </div>
        )}

        {submitError && (
          <Notice variant="error" className="hi-ui-hosting-package-type-edit-page__submit-error">
            {submitError}
          </Notice>
        )}

        <div className="hi-ui-hosting-package-type-edit-page__footer">
          {activeStep !== 'features' && (
            <Button variant="tertiary" onClick={handleBack} disabled={submitting}>
              Back
            </Button>
          )}
          {activeStep !== 'web-apps' && (
            <Button variant="primary" onClick={handleNext} disabled={submitting}>
              Next
            </Button>
          )}
          {activeStep === 'web-apps' && (
            <Button type="submit" variant="primary" loading={submitting}>
              {form.delete ? 'Delete package' : 'Save changes'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};
